import React, { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader } from "./ui/card";
import { dashboardApi } from "../services/dashboardApi";

const ESP_NAMES: Record<string, string> = {
    gr: "GetResponse", feedblitz: "FeedBlitz", fb: "FeedBlitz", mailerlite: "MailerLite", ml: "MailerLite",
    moosend: "Moosend", ms: "Moosend", br: "Brevo",
};

const METRICS: { key: string; label: string; digits: number }[] = [
    { key: "complaints", label: "Spam complaints", digits: 3 },
    { key: "hard_bounces", label: "Hard bounces", digits: 2 },
    { key: "unsubscribes", label: "Unsubscribes", digits: 2 },
];

// Status always shows icon + label, never color alone
const LEVEL_STYLE: Record<string, { cls: string; icon: string }> = {
    green: { cls: "text-green-700", icon: "✓" },
    amber: { cls: "text-yellow-700 font-semibold", icon: "⚠" },
    red: { cls: "text-red-700 font-semibold", icon: "✗" },
};

type Ceiling = { amber: number; red: number };

function espLabel(id: string): string {
    const i = id.lastIndexOf("_");
    return i < 0 ? id : `${ESP_NAMES[id.slice(0, i)] || id.slice(0, i)} ${id.slice(i + 1).toUpperCase()}`;
}

function num(v: number | null | undefined): string {
    return (v || 0).toLocaleString();
}

function pct(v: number | null | undefined, digits: number): string {
    return v == null ? "—" : `${(v * 100).toFixed(digits)}%`;
}

function levelOf(v: number | null | undefined, ceiling?: Ceiling): string {
    if (v == null || !ceiling) return "green";
    return v > ceiling.red ? "red" : v > ceiling.amber ? "amber" : "green";
}

function dmarcLevel(passRate: number | null | undefined): string {
    if (passRate == null) return "green";
    return passRate >= 0.98 ? "green" : passRate >= 0.9 ? "amber" : "red";
}

function stamp(iso?: string | null): string {
    if (!iso) return "";
    try { return format(parseISO(iso), "MMM d, HH:mm"); } catch { return iso; }
}

function Rate({ value, count, digits, ceiling }: { value: number | null; count?: number; digits: number; ceiling?: Ceiling }) {
    const st = LEVEL_STYLE[levelOf(value, ceiling)];
    return (
        <td className="py-2 px-3 text-right whitespace-nowrap">
            <span className={ceiling ? st.cls : "text-gray-800"}>{ceiling ? `${st.icon} ` : ""}{pct(value, digits)}</span>
            {count != null && <span className="text-xs text-gray-400 ml-1">({num(count)})</span>}
        </td>
    );
}

function Empty({ children }: { children: React.ReactNode }) {
    return <p className="text-sm text-gray-500 text-center py-6 px-4">{children}</p>;
}

function EspSection({ esp }: { esp: any }) {
    const rows: any[] = esp?.esps || [];
    const flags: any[] = [...(esp?.flags || [])].sort((a, b) => b.day.localeCompare(a.day));
    const ceilings: Record<string, Ceiling> = esp?.ceilings || {};
    return (
        <Card className="shadow-md border-gray-200">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50 pb-2">
                <div className="flex items-baseline justify-between gap-4">
                    <h2 className="text-lg font-bold">📬 Complaints, bounces and unsubscribes per ESP</h2>
                    {esp?.last_fetch && <span className="text-xs text-gray-400">Updated {stamp(esp.last_fetch)}</span>}
                </div>
                <p className="text-xs text-gray-500">
                    Scheduled sends from the last {esp?.days ?? 30} days, from each ESP&apos;s own reports. Complaints are the
                    spam reports ESPs get from Microsoft, Yahoo and other inbox providers. Gmail doesn&apos;t pass these on,
                    so Gmail&apos;s spam rate is only in Postmaster Tools. Human clicks come from our redirect, bots removed.
                </p>
            </CardHeader>
            <CardContent className="p-0">
                {esp?.error ? (
                    <Empty>Not running yet. The daily ESP stats job creates this data on its first run.</Empty>
                ) : rows.length === 0 ? (
                    <Empty>No sends with stats in this period.</Empty>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50/50 text-xs text-gray-600">
                                    <th className="py-2.5 px-4 text-left font-semibold">ESP</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">Sends</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">Emails sent</th>
                                    {METRICS.map((m) => (
                                        <th key={m.key} className="py-2.5 px-3 text-right font-semibold">{m.label}</th>
                                    ))}
                                    <th className="py-2.5 px-3 text-right font-semibold">Human clicks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.esp_id} className="border-b border-gray-100">
                                        <td className="py-2 px-4 font-medium">{espLabel(r.esp_id)}</td>
                                        <td className="py-2 px-3 text-right text-gray-600">{num(r.sends)}</td>
                                        <td className="py-2 px-3 text-right">{num(r.sent)}</td>
                                        {METRICS.map((m) => (
                                            <Rate key={m.key} value={r[`${m.key}_rate`]} count={r[m.key]} digits={m.digits}
                                                  ceiling={ceilings[m.key]} />
                                        ))}
                                        <Rate value={r.human_clicks_rate} count={r.human_clicks} digits={2} />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {flags.length > 0 && (
                    <div className="border-t border-gray-200 px-4 py-3">
                        <h3 className="text-sm font-semibold mb-1.5">Days over a limit</h3>
                        <ul className="space-y-1 text-sm">
                            {flags.map((f) => {
                                const st = LEVEL_STYLE[f.level] || LEVEL_STYLE.amber;
                                const metric = METRICS.find((m) => m.key === f.metric);
                                return (
                                    <li key={`${f.esp_id}-${f.day}-${f.metric}`}>
                                        <span className={st.cls}>{st.icon}</span> {f.day} · {espLabel(f.esp_id)} ·{" "}
                                        {metric?.label || f.metric} <span className={st.cls}>{pct(f.rate, metric?.digits ?? 2)}</span>
                                        <span className="text-gray-400"> ({num(f.count)} of {num(f.sent)})</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
                {Object.keys(ceilings).length > 0 && (
                    <p className="text-xs text-gray-400 px-4 py-2 border-t border-gray-100">
                        Limits per ESP and day, warning / problem:{" "}
                        {METRICS.filter((m) => ceilings[m.key]).map((m) =>
                            `${m.label.toLowerCase()} ${pct(ceilings[m.key].amber, m.digits)} / ${pct(ceilings[m.key].red, m.digits)}`
                        ).join(", ")}. A day over the problem line is emailed once.
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

function DmarcSection({ dmarc }: { dmarc: any }) {
    const rows: any[] = dmarc?.domains || [];
    return (
        <Card className="shadow-md border-gray-200">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-emerald-50 pb-2">
                <h2 className="text-lg font-bold">🛡️ DMARC per sending domain</h2>
                <p className="text-xs text-gray-500">
                    From the daily reports Gmail, Yahoo, Microsoft and others send for each domain. A message passes when
                    DKIM or SPF passes for the From domain. Failing sources are often forwarders; a large unknown source can
                    be someone else sending as the domain.
                </p>
            </CardHeader>
            <CardContent className="p-0">
                {dmarc?.error ? (
                    <Empty>Not running yet. The daily DMARC report import creates this data on its first run.</Empty>
                ) : rows.length === 0 ? (
                    <Empty>
                        No reports yet. Inbox providers send one a day per domain, so the first ones arrive a day or two
                        after a domain starts asking for them.
                    </Empty>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50/50 text-xs text-gray-600">
                                    <th className="py-2.5 px-4 text-left font-semibold">Domain</th>
                                    <th className="py-2.5 px-3 text-left font-semibold">Policy</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">Messages</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">DMARC pass</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">Reporters</th>
                                    <th className="py-2.5 px-4 text-left font-semibold">Biggest failing sources</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const st = LEVEL_STYLE[dmarcLevel(r.pass_rate)];
                                    return (
                                        <tr key={r.domain} className="border-b border-gray-100 align-top">
                                            <td className="py-2 px-4 font-medium">{r.domain}</td>
                                            <td className="py-2 px-3 text-gray-600">{r.policy || "—"}</td>
                                            <td className="py-2 px-3 text-right">{num(r.messages)}</td>
                                            <td className="py-2 px-3 text-right whitespace-nowrap">
                                                <span className={st.cls}>{st.icon} {pct(r.pass_rate, 1)}</span>
                                            </td>
                                            <td className="py-2 px-3 text-right text-gray-600">{num(r.reporters)}</td>
                                            <td className="py-2 px-4 text-xs text-gray-600">
                                                {(r.top_failing || []).slice(0, 3).map((f: any) => (
                                                    <div key={`${f.source_ip}-${f.dkim_domain}-${f.spf_domain}`}>
                                                        {f.source_ip} · {f.dkim_domain || f.spf_domain || "no DKIM/SPF"} ·{" "}
                                                        {num(f.messages)}
                                                    </div>
                                                ))}
                                                {!(r.top_failing || []).length && "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function spamLevel(rate: number | null | undefined, lines?: { amber: number; red: number }): string {
    if (rate == null || !lines) return "green";
    return rate > lines.red ? "red" : rate > lines.amber ? "amber" : "green";
}

function PostmasterSection({ pm }: { pm: any }) {
    const rows: any[] = pm?.domains || [];
    const lines = pm?.lines;
    return (
        <Card className="shadow-md border-gray-200">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-red-50 pb-2">
                <h2 className="text-lg font-bold">📮 Gmail spam rate per sending domain</h2>
                <p className="text-xs text-gray-500">
                    From Google Postmaster, one to two days behind. Gmail starts filtering at 0.3%. Only days with enough
                    Gmail volume get a number, so small domains show gaps.
                </p>
            </CardHeader>
            <CardContent className="p-0">
                {pm?.error ? (
                    <Empty>Not running yet. The daily Postmaster pull creates this data on its first run.</Empty>
                ) : rows.length === 0 ? (
                    <Empty>No Postmaster data yet for the last {pm?.days ?? 14} days.</Empty>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50/50 text-xs text-gray-600">
                                    <th className="py-2.5 px-4 text-left font-semibold">Domain</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">Latest spam rate</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">Worst in period</th>
                                    <th className="py-2.5 px-3 text-right font-semibold">Days with data</th>
                                    <th className="py-2.5 px-3 text-left font-semibold">Latest day</th>
                                    <th className="py-2.5 px-4 text-left font-semibold">Gmail verdict</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const latest = LEVEL_STYLE[spamLevel(r.latest_spam_rate, lines)];
                                    const worst = LEVEL_STYLE[spamLevel(r.worst_spam_rate, lines)];
                                    return (
                                        <tr key={r.domain} className="border-b border-gray-100">
                                            <td className="py-2 px-4 font-medium">{r.domain}</td>
                                            <td className="py-2 px-3 text-right whitespace-nowrap">
                                                <span className={latest.cls}>{r.latest_spam_rate == null ? "—" : `${latest.icon} ${pct(r.latest_spam_rate, 2)}`}</span>
                                            </td>
                                            <td className="py-2 px-3 text-right whitespace-nowrap">
                                                <span className={worst.cls}>{r.worst_spam_rate == null ? "—" : `${worst.icon} ${pct(r.worst_spam_rate, 2)}`}</span>
                                            </td>
                                            <td className="py-2 px-3 text-right text-gray-600">{num(r.days_with_data)}</td>
                                            <td className="py-2 px-3 text-gray-600">{r.latest_day}</td>
                                            <td className="py-2 px-4 text-xs text-gray-600">
                                                {r.verdict || "—"}
                                                {r.needs_work?.length ? ` · needs work: ${r.needs_work.join(", ")}` : ""}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export function DeliverabilityPanel() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        dashboardApi.getDeliverabilitySummary(days).then((res) => {
            if (!cancelled) setData(res);
        }).catch((e) => {
            if (!cancelled) setError(e?.message || "Failed to load");
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [days]);

    return (
        <div className="space-y-6">
            <div className="flex justify-end gap-1">
                {[7, 14, 30].map((d) => (
                    <button
                        key={d}
                        onClick={() => setDays(d)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md border ${days === d
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                    >
                        {d} days
                    </button>
                ))}
            </div>
            {loading ? (
                <div className="space-y-6 animate-pulse">
                    <div className="h-64 bg-gray-200 rounded-lg" />
                    <div className="h-48 bg-gray-200 rounded-lg" />
                </div>
            ) : error || !data ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center text-red-600">
                    {error || "No data"}
                </div>
            ) : (
                <>
                    <EspSection esp={data.esp} />
                    <PostmasterSection pm={data.postmaster} />
                    <DmarcSection dmarc={data.dmarc} />
                </>
            )}
        </div>
    );
}
